import React, { useRef } from "react";
import cx from 'classnames';
import {Body} from "./components/body";
import {Header} from "./components/header";
import { useController } from "./table.controller";
import { ContextMenu } from "./components/context-menu/context-menu";
import {
  SpreadsheetTableProps,
  SpreadsheetTableFilterType,
  SpreadsheetTableFilterModalState,
  SpreadsheetTableCurrentFilters,
  SpreadsheetTableFilterConfig,
  FILTER_OPERATORS
} from "./table.types";
import {Filters} from "@common/components/new-table/components/filters";
import { NewTableFilterValue, NewTableFilterApplyType, NewTableFilterType } from "@common/components/new-table/new-table.types";
import {useMessage} from "@common/hooks/message";

import './table.styles.scss';
import {formatDate, parseDate} from "@common/utils/date";
import { SPREADSHEET_FIELD_CONTEXT } from "@modules/spreadsheets/const/common";
import { SpreadsheetType } from "@modules/spreadsheets/dto/spreadsheet/type";

// Type conversion functions to bridge between SpreadsheetTable and NewTable types
const convertFilterValueToNewTable = (value: any): NewTableFilterValue => {
  if (!value) return {};
  
  return {
    operator1: value.operator1,
    whereClause1: value.whereClause1,
    andOR: value.andOR === '0' ? NewTableFilterApplyType.and : 
           value.andOR === '1' ? NewTableFilterApplyType.or : undefined,
    operator2: value.operator2,
    whereClause2: value.whereClause2,
  };
};

const convertFilterTypeToNewTable = (type?: SpreadsheetTableFilterType): NewTableFilterType | undefined => {
  if (!type) return undefined;
  
  // Map SpreadsheetTableFilterType to NewTableFilterType
  const typeMap: Record<SpreadsheetTableFilterType, NewTableFilterType> = {
    [SpreadsheetTableFilterType.text]: NewTableFilterType.text,
    [SpreadsheetTableFilterType.date]: NewTableFilterType.date,
    [SpreadsheetTableFilterType.number]: NewTableFilterType.number,
    [SpreadsheetTableFilterType.checkbox]: NewTableFilterType.checkbox,
    [SpreadsheetTableFilterType.dropdown]: NewTableFilterType.dropdown,
    [SpreadsheetTableFilterType.image]: NewTableFilterType.image,
    [SpreadsheetTableFilterType.lookupTable]: NewTableFilterType.lookupTable,
    [SpreadsheetTableFilterType.table]: NewTableFilterType.table,
    [SpreadsheetTableFilterType.computed]: NewTableFilterType.computed,
    [SpreadsheetTableFilterType.summary]: NewTableFilterType.summary,
    [SpreadsheetTableFilterType.tableSummary]: NewTableFilterType.tableSummary,
    [SpreadsheetTableFilterType.hyperlink]: NewTableFilterType.hyperlink,
    [SpreadsheetTableFilterType.integer]: NewTableFilterType.integer,
    [SpreadsheetTableFilterType.folder]: NewTableFilterType.folder,
  };
  
  return typeMap[type];
};

/**
 * Main spreadsheet table component
 * Handles data display, filtering, column resizing, and filter modal management
 */
export const SpreadsheetTable: React.FC<SpreadsheetTableProps> = (props) => {
  const { 
    className, 
    spreadsheetId, 
    spreadsheetType,
    undo,
    redo,
    canUndo,
    canRedo,
    isProcessingUndoRedo,
    isDeletingRow,
    isAddingRow
  } = props;
  const { showMessage } = useMessage();

  /**
   * Filter modal state management
   * Controls the visibility and configuration of the filter modal
   */
  const [filterModal, setFilterModal] = React.useState<SpreadsheetTableFilterModalState>({
    isOpen: false,
    columnId: null,
    columnName: null,
  });

  /**
   * Current active filters state
   * Stores the active filters for each column to show filter indicators
   */
  const [currentFilters, setCurrentFilters] = React.useState<SpreadsheetTableCurrentFilters>({});

  /**
   * Get table controller with data management methods
   * Handles data loading, column management, and filter operations
   */
  const {
    columnsWidth,
    handleLoadData,
    columns,
    data,
    setColumnsWidth,
    selectionState,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellMouseUp,
    isCellSelected,
    handleContextMenu,
    closeContextMenu,
    contextMenu,
    handleCopy,
    handleCut,
    handlePaste,
    handleFillDown,
    handleFillUp,
    handleOpenDatasheet,
    handleOpenPedigree,
    pageSize,
    handleLoadDataWithFilter,
    handleClearColumnFilter,
    handleColumnWidthChange,
    loading,
    isLoading,
    hasRrn,
    hasParentField,
    canAddRow,
    canDeleteRow,
    handleCellValueChange,
    smartColumns,
    dataResult,
    totalCount,
    selectedRowData,
    rowSelectionState,
    selectedColumnIndex,
    clearSelection,
    queryDetailsRef,
    activeFilterRef,
    setCellToUpdate,
    handleTableClick,
    isUpdatingValue,
    handleSpreadsheetClick,
    handleSpreadsheetFocus,
    filterVersion,
  } = useController(props);

  const filterSyncSignatureRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!columns || columns.length === 0) {
      return;
    }

    const filter = activeFilterRef.current;
    const signature = filter && Array.isArray(filter.whereClauses)
      ? JSON.stringify(filter.whereClauses)
      : "";

    if (filterSyncSignatureRef.current === signature) {
      return;
    }
    filterSyncSignatureRef.current = signature;

    if (!filter || !Array.isArray(filter.whereClauses) || filter.whereClauses.length === 0) {
      setCurrentFilters({});
      return;
    }

    const columnClauseMap = new Map<number, any[]>();
    filter.whereClauses.forEach((clause: any) => {
      const columnId = clause?.field?.fieldid;
      if (!columnId) {
        return;
      }
      if (!columnClauseMap.has(columnId)) {
        columnClauseMap.set(columnId, []);
      }
      columnClauseMap.get(columnId)!.push(clause);
    });

    const nextFilters: SpreadsheetTableCurrentFilters = {};

    columnClauseMap.forEach((clauses, columnId) => {
      if (!clauses.length) {
        return;
      }

      const sortedClauses = [...clauses].slice(0, 2);
      const filterValue: any = {};

      if (sortedClauses[0]) {
        const clause = sortedClauses[0];
        filterValue.operator1 = clause.operator !== undefined ? String(clause.operator) : undefined;
        filterValue.whereClause1 = clause.value;
        const clauseAndOr = clause.andOr ?? clause.andOR;
        if (clauseAndOr !== undefined && clauseAndOr !== null) {
          filterValue.andOR = clauseAndOr === "OR" || clauseAndOr === 1 || clauseAndOr === "1"
            ? "1"
            : clauseAndOr === "AND" || clauseAndOr === 0 || clauseAndOr === "0"
              ? "0"
              : undefined;
        }
      }

      if (sortedClauses[1]) {
        const clause = sortedClauses[1];
        filterValue.operator2 = clause.operator !== undefined ? String(clause.operator) : undefined;
        filterValue.whereClause2 = clause.value;
        const clauseAndOr = clause.andOr ?? clause.andOR;
        if (clauseAndOr !== undefined && clauseAndOr !== null) {
          filterValue.andOR = clauseAndOr === "OR" || clauseAndOr === 1 || clauseAndOr === "1"
            ? "1"
            : clauseAndOr === "AND" || clauseAndOr === 0 || clauseAndOr === "0"
              ? "0"
              : filterValue.andOR;
        }
      }

      if (
        filterValue.operator1 !== undefined ||
        filterValue.whereClause1 !== undefined ||
        filterValue.operator2 !== undefined ||
        filterValue.whereClause2 !== undefined
      ) {
        nextFilters[columnId] = filterValue;
      }
    });

    setCurrentFilters(prev => {
      const prevSignature = JSON.stringify(prev || {});
      const nextSignature = JSON.stringify(nextFilters);
      if (prevSignature === nextSignature) {
        return prev;
      }
      return nextFilters;
    });
  }, [filterVersion, columns, activeFilterRef]);

  /**
   * Reset filters when spreadsheet changes
   * Clears all filters and closes filter modal when switching spreadsheets
   */
  React.useEffect(() => {
    setCurrentFilters({});
    setFilterModal({
      isOpen: false,
      columnId: null,
      columnName: null,
    });
  }, [spreadsheetId]);

  // Pass selectedColumnIndex to parent controller
  React.useEffect(() => {
    if (props.onSelectedColumnIndexChange) {
      props.onSelectedColumnIndexChange(selectedColumnIndex);
    }
  }, [selectedColumnIndex, props.onSelectedColumnIndexChange]);

  // Expose controller refs to parent component
  React.useEffect(() => {
    if (props.onControllerRefsReady) {
      props.onControllerRefsReady({
        queryDetailsRef,
        activeFilterRef
      });
    }
  }, [props.onControllerRefsReady, queryDetailsRef, activeFilterRef]);

  /**
   * Open filter modal for a specific column
   * Sets the modal state to show filter options for the selected column
   */
  const handleFilterOpen = React.useCallback((columnId: number, columnName: string) => {
    setFilterModal({
      isOpen: true,
      columnId,
      columnName,
    });
  }, []);

  /**
   * Close filter modal
   * Resets modal state to hide the filter interface
   */
  const handleFilterClose = React.useCallback(() => {
    setFilterModal({
      isOpen: false,
      columnId: null,
      columnName: null,
    });
  }, []);

  /**
   * Clear specific column filter
   * Removes filter from current filters state and reloads data without that filter
   */
  const handleClearColumnFilterClick = React.useCallback((columnId: number) => {
    setCurrentFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });

    // Clear the specific column filter and reload data
    handleClearColumnFilter(columnId);
  }, [handleClearColumnFilter]);

  /**
   * Get filter type and options for the current filter column
   * Now directly uses SpreadsheetTableFilterType without conversion
   */
  const getFilterConfig = (): SpreadsheetTableFilterConfig => {
    if (!filterModal.columnId) return { type: undefined, options: undefined };
    const column = columns.find(col => col.id === filterModal.columnId);

    if (!column) return { type: undefined, options: undefined };

    return {
      type: column.filter?.type || SpreadsheetTableFilterType.text,
      options: column.dropdownOptions,
      lookupValues: column?.lookupValues || {}
    };
  };

  // Helper function to convert dropdown values for backend
  const convertDropdownValue = (value: any, column: any) => {
    if (column?.filter?.type === SpreadsheetTableFilterType.dropdown && column?.dropdownOptions && value) {
      const dropdownOption = column.dropdownOptions.find((option: any) => option.actualValue === value);
      if (dropdownOption) {
        // Special handling for specific dropdown values
        if(column.header === 'Maternal-Paternal') {
        if (dropdownOption.displayValue === 'Mat/Maternal') {
          return 'Mat';
        } else if (dropdownOption.displayValue === 'Pat/Paternal') {
          return 'Pat';
        } else {
          return dropdownOption.displayValue;
        }
      }
      else {
        return value;
      }
      }
    }
    return value;
  };

  /**
   * Transform filterValue to ISS API format and call data load
   * Converts filter values from UI format to API format and applies the filter
   */
  const handleFilterApply = React.useCallback((filterValue: any) => {
    if (filterModal.columnId && filterValue) {
      // Find column metadata for filter configuration
      const column = columns.find(col => col.id === filterModal.columnId);
      const newWhereClauses: any[] = [];


    // Check if there will be multiple clauses
    const hasSecondClause = filterValue.operator2 && filterValue.whereClause2;

    // Helper function to format date values
    const formatDateValue = (dateValue: string) => {
      if (!dateValue) return dateValue;
        // For regular date fields, convert from MM/DD/YYYY to YYYY-MM-DD for API
        try {
          const parsedDate = parseDate(dateValue, 'MM/DD/YYYY');
          return formatDate(parsedDate, { format: 'YYYY/MM/DD' });
        } catch (e) {
          console.warn('Failed to parse date:', dateValue, e);
        }
        return dateValue;
      
    };

    // Process first filter condition
      if (filterValue.operator1 && (filterValue.whereClause1 ||
          filterValue.operator1 == 7 || filterValue.operator1 == 8)) {
      // Convert date format if needed for date fields
      let value = filterValue.whereClause1;
      if (column?.filter?.type === SpreadsheetTableFilterType.date && value) {
        value = formatDateValue(value);
      }

      // Handle checkbox fields - convert boolean to Y/N
      if (column?.typeId === 4 ) {
        if (value === true || value === 'true' || value === 'Y') {
          value = 'Y';
        } else if (value === false || value === 'false' || value === 'N' || value === undefined || value === null) {
          value = 'N';
        }
      }

      // Handle dropdown fields - convert actualValue to displayValue for backend
      value = convertDropdownValue(value, column);

      // Build the new format with field and parentField objects
      const clause: any = {
        field: {
          fieldid: column?.id,
          type: column?.typeId , // Default to type 5 for most fields
          fieldcontext: spreadsheetType === SpreadsheetType.individual ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
          system: column?.system 
        },
        fieldClass:column?.fieldClass,
        operator: parseInt(filterValue.operator1),
        value: value,
      };

      // Add parentField if column has parentField
      if (column?.parentField && column.parentField.fieldid) {
        clause.parentField = {
          fieldid: column.parentField.fieldid,
          type: column.parentField.type || 8, // Default type for parent fields
          fieldcontext: spreadsheetType === SpreadsheetType.individual ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
          system: column.parentField.system || false
        };
      }

      // Add andOr if present
      if (filterValue.andOR !== undefined) {
        clause.andOr = filterValue.andOR == "0" ? "AND" : filterValue.andOR == "1" ? "OR" : null;
      }

      // Add parenthesis: first clause gets leftParenthesis if there's a second clause
      if (hasSecondClause) {
        clause.leftParenthesis = true;
        clause.rightParenthesis = false;
      }
      else {
        clause.leftParenthesis = false;
        clause.rightParenthesis = false;
      }

      newWhereClauses.push(clause);
    }

    // Process second filter condition (if exists)
    if (filterValue.operator2 && filterValue.whereClause2) {
      // Convert date format if needed
      let value = filterValue.whereClause2;
      if (column?.filter?.type === SpreadsheetTableFilterType.date && value) {
        value = formatDateValue(value);
      }

      // Handle checkbox fields for second clause - convert boolean to Y/N
      if (column?.typeId === 4) {
        if (value === true || value === 'true' || value === 'Y') {
          value = 'Y';
        } else if (value === false || value === 'false' || value === 'N' || value === undefined || value === null) {
          value = 'N';
        }
      }

      // Handle dropdown fields for second clause - convert actualValue to displayValue for backend
      value = convertDropdownValue(value, column);

      // Build the new format with field and parentField objects
      const clause: any = {
        field: {
          fieldid: column?.id,
          type: column?.typeId, // Default to type 5 for most fields
          fieldcontext: spreadsheetType === SpreadsheetType.individual ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
          system: column?.system
        },
        fieldClass:column?.fieldClass,
        operator: parseInt(filterValue.operator2),
        value: value,
      };

      // Add parentField if column has parentField
      if (column?.parentField && column.parentField.fieldid) {
        clause.parentField = {
          fieldid: column.parentField.fieldid,
          type: column.parentField.type , // Default type for parent fields
          fieldcontext: spreadsheetType === SpreadsheetType.individual ? SPREADSHEET_FIELD_CONTEXT.INDIVIDUAL : SPREADSHEET_FIELD_CONTEXT.PEDIGREE,
          system: column.parentField.system
        };
      }

      // Add parenthesis: second clause gets rightParenthesis
      clause.leftParenthesis = false;
      clause.rightParenthesis = true;

      newWhereClauses.push(clause);
    }

    // Store in current filters for UI display
    setCurrentFilters(prev => ({
      ...prev,
      [filterModal.columnId as number]: filterValue
    }));

    // Create new filter with the new clauses
    const newFilter = {
      whereClauses: newWhereClauses,
    };

    // Use handleLoadDataWithFilter which will merge with existing filters
    handleLoadDataWithFilter({ filter: newFilter });
  }
  handleFilterClose();
}, [filterModal.columnId, filterModal.columnName, columns, handleFilterClose, handleLoadDataWithFilter]);

  // Get filter configuration for the current column
  const filterConfig = getFilterConfig();

  // Pass dependencies to parent controller
  React.useEffect(() => {
    if (props.onCurrentDataChange) {
      props.onCurrentDataChange(data);
    }
  }, [data, props.onCurrentDataChange]);

  React.useEffect(() => {
    if (props.onSmartColumnsChange) {
      props.onSmartColumnsChange(smartColumns);
    }
  }, [smartColumns, props.onSmartColumnsChange]);

  React.useEffect(() => {
    if (props.onDataResultChange && dataResult) {
      props.onDataResultChange(dataResult);
    }
  }, [dataResult, props.onDataResultChange]);

  React.useEffect(() => {
    if (props.onTotalCountChange) {
      props.onTotalCountChange(totalCount);
    }
  }, [totalCount, props.onTotalCountChange]);

  React.useEffect(() => {
    if (props.onSelectedRowDataChange) {
      props.onSelectedRowDataChange(selectedRowData);
    }
  }, [selectedRowData, props.onSelectedRowDataChange]);

  React.useEffect(() => {
    if (props.onHasRrnChange) {
      props.onHasRrnChange(hasRrn);
    }
  }, [hasRrn, props.onHasRrnChange]);
  
  React.useEffect(() => {
    if (props.onHasParentFieldChange) {
      props.onHasParentFieldChange(hasParentField);
    }
  }, [hasParentField, props.onHasParentFieldChange]);

  React.useEffect(() => {
    if (props.onCanAddRowChange) {
      props.onCanAddRowChange(canAddRow);
    }
  }, [canAddRow, props.onCanAddRowChange]);

  React.useEffect(() => {
    if (props.onCanDeleteRowChange) {
      props.onCanDeleteRowChange(canDeleteRow);
    }
  }, [canDeleteRow, props.onCanDeleteRowChange]);

  // Notify parent of undo/redo state changes
  React.useEffect(() => {
    
    if (props.onUndoRedoStateChange) {
      props.onUndoRedoStateChange({
        canUndo: canUndo || false,
        canRedo: canRedo || false,
        onUndo: undo || (() => {}),
        onRedo: redo || (() => {}),
      });
    }
  }, [canUndo, canRedo, undo, redo, props.onUndoRedoStateChange]);

  const tableRootRef = useRef<HTMLDivElement>(null);
  
  // Helper to check if event target is inside a dropdown, datepicker, or filter modal
  const isDropdownOrDatepickerElement = React.useCallback((target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Element)) return false;

    // Check for react-select dropdown menu (portal rendered in body)
    // React-select uses these class patterns for its menu
    if (target.closest('[class*="react-select"]') ||
        target.closest('[id*="react-select"]') ||
        target.closest('.ss-cell-select') ||
        target.closest('[class*="ss-cell-select"]') ||
        // Check for react-select menu list specifically
        target.closest('[class*="menu"]') && target.closest('[class*="react-select"]')) {
      return true;
    }

    // Check for react-datepicker dropdown
    if (target.closest('.react-datepicker') ||
        target.closest('.react-datepicker__portal') ||
        target.closest('[class*="react-datepicker"]')) {
      return true;
    }

    // Check for lookup table dropdown
    if (target.closest('.lookup-table-dropdown')) {
      return true;
    }

    // Check for filter modal elements (text inputs, dropdowns, etc.)
    if (target.closest('.NewTableFilters')) {
      return true;
    }

    return false;
  }, []);

  // Add global click handler to detect clicks on portal-rendered dropdown menus
  // This ensures the spreadsheet is marked as active when interacting with dropdowns
  // but prevents focusing the table root which would close the dropdown
  React.useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as Element;
      // If clicking on a dropdown menu in a portal, set active spreadsheet ID
      // (for copy/paste functionality) but don't focus table to avoid closing dropdown
      if (isDropdownOrDatepickerElement(target)) {
        // Just set the active spreadsheet ID, don't trigger focus
        handleSpreadsheetClick();
      }
    };

    document.addEventListener('mousedown', handleGlobalClick, true);
    return () => {
      document.removeEventListener('mousedown', handleGlobalClick, true);
    };
  }, [isDropdownOrDatepickerElement, handleSpreadsheetClick]);

  // Helper to check if any cell is currently being edited
  const isCellBeingEdited = React.useCallback((): boolean => {
    const activeElement = document.activeElement as Element | null;
    if (!activeElement) return false;

    // If active element is within a cell (has row/column dataset), consider it editing when it's an input-like element
    const cellElement = activeElement.closest('[data-row-index][data-column-id]');
    if (!cellElement) return false;

    // Editable input elements or embedded editors
    if (
      activeElement instanceof HTMLInputElement ||
      activeElement instanceof HTMLTextAreaElement ||
      activeElement instanceof HTMLSelectElement ||
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT' ||
      !!activeElement.closest('[class*="react-select"]') ||
      !!activeElement.closest('[class*="react-datepicker"]')
    ) {
      return true;
    }

    return false;
  }, []);

  // Activate spreadsheet and focus root div (only if not interacting with dropdowns or editing a cell)
  const handleActivateSpreadsheet = React.useCallback((e?: React.MouseEvent) => {
    // If event is provided, check if click was on dropdown
    if (e && isDropdownOrDatepickerElement(e.target)) {
      return;
    }

    handleSpreadsheetClick();

    // Determine if the current activation came from clicking inside a data cell.
    const clickedInsideCell =
      e &&
      e.target instanceof Element &&
      !!e.target.closest('[data-row-index][data-column-id]');

    // Only focus if not currently interacting with a dropdown
    // Check if any dropdown menu is open
    const hasOpenDropdown =
      document.querySelector('[class*="react-select"] [class*="menu"]') ||
      document.querySelector('.react-datepicker__portal') ||
      document.querySelector('.lookup-table-dropdown') ||
      document.querySelector('.NewTableFilters');

    // Avoid focusing the table while actively editing a cell to prevent blue focus outline
    // Also skip shifting focus when activation originated from a cell so the cell keeps focus.
    if (
      e &&
      !clickedInsideCell &&
      !hasOpenDropdown &&
      !isCellBeingEdited() &&
      tableRootRef.current
    ) {
      tableRootRef.current.focus();
    }
  }, [handleSpreadsheetClick, isDropdownOrDatepickerElement, isCellBeingEdited]);

  return (
      <div 
      ref={tableRootRef}
      className={cx("SpreadsheetDetailsSpreadsheetResultTable", className)}
      onContextMenu={handleContextMenu}
      onClick={(e) => {
        handleTableClick(e);
        handleActivateSpreadsheet(e);
      }}
      onFocus={(e) => {
        // Only activate if focus is not from a dropdown element
        if (!isDropdownOrDatepickerElement(e.target)) {
          handleSpreadsheetFocus();
        }
      }}
      tabIndex={0}
    >
        <div className="SpreadsheetDetailsSpreadsheetResultTable__inner">
          {/* Common loading indicator */}
        {(isLoading || isProcessingUndoRedo || isDeletingRow || isAddingRow || isUpdatingValue) && (
            <div className="SpreadsheetDetailsSpreadsheetResultTable__loading">
              <div className="SpreadsheetDetailsSpreadsheetResultTable__loading__spinner">
                <i className="fa fa-spinner fa-spin"></i>
              </div>
              <div className="SpreadsheetDetailsSpreadsheetResultTable__loading__text">
                Loading...
              </div>
            </div>
          )}
          
          {/* Table header with filter controls and column resizing */}
          <Header
              onFilter={handleFilterOpen}
              onColumnsResize={handleColumnWidthChange}
              columnsWidth={columnsWidth}
              currentFilters={currentFilters}
              onClearColumnFilter={handleClearColumnFilterClick}
              className="SpreadsheetDetailsSpreadsheetResultTable__header"
              columns={columns}
              spreadsheetType={props.spreadsheetType}
          />
          <Body
              columnsWidth={columnsWidth}
              className="SpreadsheetDetailsSpreadsheetResultTable__body"
              columns={columns}
              data={data}
              spreadsheetId={spreadsheetId as number}
              onLoadData={handleLoadData}
              selectionState={selectionState}
              rowSelectionState={rowSelectionState}
              onCellMouseDown={handleCellMouseDown}
              onCellMouseEnter={handleCellMouseEnter}
              onCellMouseUp={handleCellMouseUp}
              isCellSelected={isCellSelected}
              pageSize={pageSize}
              onCellValueChange={handleCellValueChange}
              loading={loading}
              setCellToUpdate={setCellToUpdate}
              onActivateSpreadsheet={handleActivateSpreadsheet}
          />
          {filterModal.isOpen && filterModal.columnId && (
              <Filters
                  moduleName="spreadsheet-table"
                  filterName={filterModal.columnName || "Column"}
                  defaultValue={convertFilterValueToNewTable(currentFilters[filterModal.columnId as number] || {})}
                  filters={FILTER_OPERATORS}
                  showMessage={showMessage}
                  onClose={handleFilterClose}
                  onApply={(value?: NewTableFilterValue) => {
                    // Convert back to SpreadsheetTableFilterValue format
                    const convertedValue = value ? {
                      operator1: value.operator1,
                      whereClause1: value.whereClause1,
                      andOR: value.andOR === NewTableFilterApplyType.and ? '0' : 
                             value.andOR === NewTableFilterApplyType.or ? '1' : undefined,
                      operator2: value.operator2,
                      whereClause2: value.whereClause2,
                    } : undefined;
                    handleFilterApply(convertedValue);
                  }}
                  type={convertFilterTypeToNewTable(filterConfig.type)}
                  options={filterConfig.options}
                  lookupValues={filterConfig.lookupValues}
                  getFolders={columns.find(col => col.id === filterModal.columnId)?.filter?.getFolders}
            
              />
          )}
        </div>
      
        {contextMenu && (
         <ContextMenu
           x={contextMenu.x}
           y={contextMenu.y}
           onClose={closeContextMenu}
           onCut={handleCut}
           onCopy={handleCopy}
           onPaste={handlePaste}
           onFillDown={handleFillDown}
           onFillUp={handleFillUp}
           onOpenDatasheet={handleOpenDatasheet}
           onOpenPedigree={handleOpenPedigree}
           canCut={!!selectionState.selection || (rowSelectionState && rowSelectionState.selectedRows.size > 0)}
           canCopy={!!selectionState.selection || (rowSelectionState && rowSelectionState.selectedRows.size > 0)}
           canPaste={true}
           canFill={!!selectionState.selection}
           canAddRow={canAddRow && !props.isAddingRow}
           canDeleteRow={canDeleteRow}
           canOpenDatasheet={!!selectionState.selection}
           canOpenPedigree={!!selectionState.selection}
           onAddRow={props.onAddRow}
           onDeleteRow={props.onDeleteRow}
         />
       )}
      </div>
  );
};